import React from 'react';
import { string, object, array } from 'prop-types';
import classNames from 'classnames';

import { NamedLink } from '../../../components';
import Field from '../../PageBuilder/Field';

import css from './ValuePropsSection.module.css';

/**
 * Special Offers / Value Propositions Section for ConstructionYardSale.com
 * Matches the Replit "Special Offers" design: two gradient promo cards.
 * Renders Console blocks as feature/promo cards with icons and CTAs.
 *
 * Console setup: Create a "features" or "columns" section with blocks,
 * each block having title, description, and optional media (icon image).
 */
const ValuePropsSection = props => {
  const {
    sectionId,
    className,
    title,
    description,
    blocks,
    options,
    ...rest
  } = props;

  const hasBlocks = blocks && blocks.length > 0;
  const hasTitle = title?.content;

  return (
    <section id={sectionId} className={classNames(css.root, className)}>
      <div className={css.container}>
        <div className={css.header}>
          {hasTitle ? (
            <Field data={title} className={css.sectionTitle} />
          ) : (
            <h2 className={css.sectionTitle}>Special Offers</h2>
          )}
          {description?.content && (
            <Field data={description} className={css.sectionDescription} />
          )}
        </div>

        {hasBlocks ? (
          <div className={css.grid}>
            {blocks.map((block, index) => (
              <div key={block.blockId || index} className={css.card}>
                {block.media && (
                  <div className={css.iconWrapper}>
                    <Field data={block.media} className={css.icon} />
                  </div>
                )}
                {block.title?.content && (
                  <Field data={block.title} className={css.cardTitle} />
                )}
                {block.text?.content && (
                  <Field data={block.text} className={css.cardText} />
                )}
                {block.callToAction?.content && (
                  <Field data={block.callToAction} className={css.cardCta} />
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className={css.grid}>
            <div className={classNames(css.card, css.cardPrimary)}>
              <div className={css.iconCircle}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 12 20 22 4 22 4 12" />
                  <rect x="2" y="7" width="20" height="5" />
                  <line x1="12" y1="22" x2="12" y2="7" />
                  <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
                  <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
                </svg>
              </div>
              <h3 className={css.cardTitle}>Sell 5 Items, Get Pro Free!</h3>
              <p className={css.cardText}>
                Successfully complete 5 sales in the next 30 days and we&apos;ll upgrade
                your account to a <strong>PRO Subscription for 3 full months</strong> absolutely free.
              </p>
              <NamedLink name="NewListingPage" className={css.cardCtaButton}>
                Start Selling Now
              </NamedLink>
            </div>
            <div className={classNames(css.card, css.cardSecondary)}>
              <div className={css.iconCircle}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <h3 className={css.cardTitle}>Refer 3 Friends, Get Pro Free!</h3>
              <p className={css.cardText}>
                Invite 3 friends to join ConstructionYardSale.com. When they create an
                account, you&apos;ll instantly receive <strong>1 month of PRO Subscription</strong> for free.
              </p>
              <NamedLink name="SignupPage" className={css.cardCtaButton}>
                Invite Friends
              </NamedLink>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

ValuePropsSection.propTypes = {
  sectionId: string.isRequired,
  className: string,
  title: object,
  description: object,
  blocks: array,
  options: object,
};

export default ValuePropsSection;
